import { Injectable } from '@angular/core'
import { EventModel, IEvent } from '../models/event.model'
import { AngularFireDatabase } from '@angular/fire/compat/database'
import { AngularFireFunctions } from '@angular/fire/compat/functions'
import { Observable, throwError } from 'rxjs'
import { map, take, catchError } from 'rxjs/operators'
import { EmailService } from './email.service'
import { Email } from '@shared/const/email/email.const'
import { AttendeesService } from './attendees.service'
import { AttendeeModel } from '../models/attendee.model'
import { UserService } from './user.service'
import {
  getUpdateLogDetails,
  getCreateLogDetails,
  getDeleteLogDetails,
  LogAction,
} from '../decorator/log-action.decorator'
import { HistoryService } from '../services/history/history.service'
import { AuthService } from './auth.service'
import { ImageStorageService } from '../services/image-storage.service'

@Injectable({
  providedIn: 'root',
})
export class EventService {
  // An event stays in the upcoming lists for 24 hours after it closes.
  static readonly UPCOMING_GRACE_MS = 24 * 60 * 60 * 1000

  static isUpcoming(event: { toDateTime?: any }, now = Date.now()): boolean {
    return new Date(event?.toDateTime).getTime() + EventService.UPCOMING_GRACE_MS > now
  }

  // By start time; equal starts in database key order (organiser, then event), as the full-tree read returned them.
  static byStart(a: EventModel, b: EventModel): number {
    const byTime = new Date(a.fromDateTime).getTime() - new Date(b.fromDateTime).getTime()
    if (byTime) {
      return byTime
    }
    const keyA = `${a.organiserId}/${a.id}`
    const keyB = `${b.organiserId}/${b.id}`
    return keyA < keyB ? -1 : keyA > keyB ? 1 : 0
  }
  // Safeguard against a token loop, far above any real list (100 000 events).
  private static readonly MAX_PAGES = 1000

  constructor(
    private db: AngularFireDatabase,
    public historyService: HistoryService,
    public authService: AuthService,
    private emailService: EmailService,
    private userService: UserService,
    private attendeesService: AttendeesService,
    private imageStorage: ImageStorageService,
    private cloudFunctions: AngularFireFunctions
  ) {}

  async update(event: IEvent, eventId: string, oldOrganiserId: string, newOrganiserId: string): Promise<void> {
    if (oldOrganiserId === newOrganiserId) {
      await this.updateEvent(event, eventId, newOrganiserId)
      return
    }

    return this.checkEventExists(eventId, newOrganiserId).then(exists => {
      if (exists) {
        throw new Error('Event already exists under the new organiser')
      }
      return this.moveEvent(event, eventId, oldOrganiserId, newOrganiserId)
    })
  }

  @LogAction((args, result) => (args[2] ? 'approve_event' : 'disapprove_event'), getUpdateLogDetails)
  async approve(
    event: IEvent,
    eventId: string,
    isApproved: boolean,
    organiserId: string
  ): Promise<{ previousData: EventModel | null }> {
    const previousData = await this.getPreviousData(organiserId, eventId)
    await this.db.object<EventModel>(`/events/${organiserId}/${eventId}/`).update({
      isApproved: isApproved,
    })
    return { previousData }
  }

  giveReasonOfDelete(reasonOfDelete: string, eventId: string, organiserId: string): Promise<void> {
    return this.db.object<EventModel>(`/events/${organiserId}/${eventId}/`).update({
      reasonOfDelete: reasonOfDelete,
    })
  }

  @LogAction('create_event', (args, result) => getCreateLogDetails(args, result))
  async create(event: IEvent, userId: string, userName: string, eventId?: string): Promise<{ key: string }> {
    const newEventId = eventId || this.db.createPushId()
    if (!newEventId) {
      throw new Error('Unable to generate event id')
    }
    await this.db.list(`/events/${userId}`).set(newEventId, event)
    this.emailService.send(
      Email.ADMIN.EMAIL,
      Email.ADMIN.EMAIL,
      Email.MESSAGE.NEW_EVENT_CREATED.SUBJECT,
      this.getNewEventCreatedEmailWithParams(event.name, userName)
    )
    return { key: newEventId }
  }

  // `from` and `to` bound the closing time of the events (the date index is keyed by it).
  fetchByDateRangePaged(params: {
    from: number
    to?: number
    pageSize?: number
    pageToken?: string
    organiserId?: string
  }): Observable<{ items: EventModel[]; nextPageToken?: string }> {
    const fn = this.cloudFunctions.httpsCallable('listEventsByDatePaginated')
    const currentUser = this.authService.getCurrentUser()
    const organiserId =
      params.organiserId || (currentUser?.isOrganization && !currentUser?.isAdmin ? currentUser.id : undefined)
    const payload = { ...params, organiserId }

    return fn(payload).pipe(
      map(response => {
        const items = Array.isArray((response as any)?.items) ? (response as any).items : []
        const mapped = items.map(
          (item: any) =>
            new EventModel({
              id: item?.eventId,
              organiserId: item?.organiserId,
              name: item?.name,
              description: item?.description,
              fromDateTime: item?.fromDateTime,
              toDateTime: item?.toDateTime,
              deadlineDateTime: item?.deadlineDateTime,
              city: item?.city,
              address: item?.address,
              maxNumberOfAttendees: item?.maxNumberOfAttendees,
              numberOfAttendees: item?.numberOfAttendees,
              money: item?.money,
              cabbage: item?.cabbage,
              salt: item?.salt,
              reasonOfDelete: item?.reasonOfDelete,
              isApproved: item?.isApproved,
              imageUrl: item?.imageUrl,
              imageStoragePath: item?.imageStoragePath,
            })
        )
        return { items: mapped, nextPageToken: (response as any)?.nextPageToken }
      })
    )
  }

  async getById(eventId: string): Promise<EventModel | null> {
    const response = await this.cloudFunctions.httpsCallable('getEventById')({ eventId }).pipe(take(1)).toPromise()
    const item = (response as any)?.event
    return item ? new EventModel({ ...item, id: item.eventId }) : null
  }

  public async duplicate(event: EventModel): Promise<EventModel | null> {
    const organiser = await this.userService.get(event.organiserId).pipe(take(1)).toPromise()
    if (!organiser) {
      return null
    }

    const newEventId = this.db.createPushId()
    if (!newEventId) {
      throw new Error('Unable to generate new event id for duplication')
    }

    const clonedImage = await this.cloneEventImage(event, newEventId)

    const duplicatedEvent: IEvent = {
      name: event.name + ' (Másolat)',
      description: event.description,
      fromDateTime: event.fromDateTime,
      toDateTime: event.toDateTime,
      deadlineDateTime: event.deadlineDateTime,
      city: event.city,
      address: event.address,
      maxNumberOfAttendees: event.maxNumberOfAttendees,
      organiserId: event.organiserId,
      money: event.money,
      cabbage: event.cabbage,
      salt: event.salt,
      isApproved: false,
      imageUrl: clonedImage?.url || null,
      imageStoragePath: clonedImage?.path || null,
    }

    await this.create(duplicatedEvent, event.organiserId, organiser.name, newEventId)
    return new EventModel({ ...duplicatedEvent, id: newEventId } as any)
  }

  private async cloneEventImage(
    event: EventModel,
    newEventId: string
  ): Promise<{ url: string; path: string } | null> {
    if (!event.imageStoragePath && !event.imageUrl) {
      return null
    }

    if (event.imageStoragePath) {
      try {
        return await this.imageStorage.cloneFromPath(event.imageStoragePath, event.organiserId, newEventId)
      } catch (err) {
        console.error('Error cloning event image from storage path:', err)
      }
    }

    if (event.imageUrl) {
      try {
        return await this.imageStorage.cloneFromUrl(event.imageUrl, event.organiserId, newEventId)
      } catch (err) {
        console.error('Error cloning event image:', err)
        throw err
      }
    }

    throw new Error('Unable to clone event image')
  }

  getNewEventCreatedEmailWithParams(eventName: string, userName: string): string {
    return Email.MESSAGE.NEW_EVENT_CREATED.HTML.replace('{eventName}', eventName).replace('{organization}', userName)
  }

  get(eventId: string, organiserId: string): Observable<EventModel> {
    if (!organiserId || !eventId) {
      return throwError('Organizer ID and Event ID are required.')
    }

    return this.db
      .object<EventModel>(`/events/${organiserId}/${eventId}/`)
      .snapshotChanges()
      .pipe(
        map(eventSnapshots => {
          const data = eventSnapshots.payload.val()
          const id = eventSnapshots.payload.key
          if (data) {
            return new EventModel({ id, ...data })
          } else {
            console.error('Event not found for path:', `/events/${organiserId}/${eventId}/`)
            throw new Error('Event not found.')
          }
        }),
        catchError(error => {
          console.error('Error in snapshotChanges:', error)
          throw error
        })
      )
  }

  @LogAction('delete_event', (args, result) => getDeleteLogDetails(args, result))
  async delete(
    eventId: string,
    event: EventModel,
    userId: string,
    shouldNotifyAttendees: boolean
  ): Promise<{ previousData: EventModel | null }> {
    const previousData = await this.getPreviousData(userId, eventId)
    await this.deleteEventImage(event?.imageStoragePath || null)
    if (!shouldNotifyAttendees) {
      await this.db.list(`/events/${userId}`).remove(event.id)
      return { previousData }
    }
    try {
      const attendees = await this.attendeesService.getAllByEventId(event.id).pipe(take(1)).toPromise()
      const organiser = await this.userService.get(event.organiserId).pipe(take(1)).toPromise()

      attendees.forEach((attendee: AttendeeModel) => {
        try {
          this.emailService.send(
            Email.ADMIN.EMAIL,
            attendee.email,
            Email.MESSAGE.DELETED_EVENT.SUBJECT,
            this.getDeletedEventWithParams(event, organiser.name, attendee.name)
          )
        } catch (emailError) {
          console.error(`Error sending email to attendee ${attendee.email}:`, emailError)
        }
      })
      try {
        this.emailService.send(
          Email.ADMIN.EMAIL,
          organiser.email,
          Email.MESSAGE.DELETED_EVENT_ORGANIZATION.SUBJECT,
          this.getDeletedEventForOrganizationWithParams(event, organiser.name)
        )
      } catch (emailError) {
        console.error(`Error sending email to organiser ${organiser.email}:`, emailError)
      }
      await this.db.list(`/events/${userId}`).remove(event.id)
      return { previousData }
    } catch (error) {
      console.error('Error deleting event:', error)
      return null
    }
  }

  private async deleteEventImage(path: string | null): Promise<void> {
    if (!path) {
      return
    }
    try {
      await this.imageStorage.deleteByPath(path)
    } catch (err) {
      console.error('Error deleting event image from storage:', err)
    }
  }

  private getDeletedEventWithParams(event: EventModel, organizationName: string, attendeeName: string): string {
    return Email.MESSAGE.DELETED_EVENT.HTML.replace('{eventName}', event.name)
      .replace('{attendeeName}', attendeeName)
      .replace('{eventCity}', event.city)
      .replace('{eventAddress}', event.address)
      .replace('{eventOrganizer}', organizationName)
      .replace('{eventDateTimeFrom}', event.fromDateTime)
      .replace('{eventDateTimeTo}', event.toDateTime)
      .replace('{reason}', event.reasonOfDelete)
  }

  private getDeletedEventForOrganizationWithParams(event: EventModel, organizationName: string): string {
    return Email.MESSAGE.DELETED_EVENT_ORGANIZATION.HTML.replace('{eventName}', event.name)
      .replace('{eventCity}', event.city)
      .replace('{eventAddress}', event.address)
      .replace('{eventOrganizer}', organizationName)
      .replace('{eventDateTimeFrom}', event.fromDateTime)
      .replace('{eventDateTimeTo}', event.toDateTime)
      .replace('{reason}', event.reasonOfDelete)
  }

  @LogAction('update_event', (args, result) => getUpdateLogDetails(args, result))
  private async updateEvent(
    event: IEvent,
    eventId: string,
    organiserId: string
  ): Promise<{ previousData: EventModel | null }> {
    const previousData = await this.getPreviousData(organiserId, eventId)
    await this.db.object<EventModel>(`/events/${organiserId}/${eventId}/`).update({
      name: event.name,
      description: event.description,
      fromDateTime: event.fromDateTime,
      toDateTime: event.toDateTime,
      deadlineDateTime: event.deadlineDateTime,
      city: event.city,
      address: event.address,
      maxNumberOfAttendees: event.maxNumberOfAttendees,
      organiserId: organiserId,
      money: event.money,
      cabbage: event.cabbage,
      salt: event.salt,
      imageUrl: event.imageUrl || null,
      imageStoragePath: event.imageStoragePath || null,
    })
    return { previousData }
  }

  private checkEventExists(eventId: string, organiserId: string): Promise<boolean> {
    return this.db
      .object<EventModel>(`/events/${organiserId}/${eventId}/`)
      .snapshotChanges()
      .pipe(take(1))
      .toPromise()
      .then(snapshot => snapshot.payload.exists())
  }

  private moveEvent(event: IEvent, eventId: string, oldOrganiserId: string, newOrganiserId: string): Promise<void> {
    const deleteOldEvent = this.db.object<EventModel>(`/events/${oldOrganiserId}/${eventId}/`).remove()
    event.organiserId = newOrganiserId
    const createNewEvent = this.db.object<IEvent>(`/events/${newOrganiserId}/${eventId}/`).set(event)
    return Promise.all([deleteOldEvent, createNewEvent]).then(() => {})
  }

  private async getPreviousData(organiserId: string, id: string) {
    const snapshot = await this.db.object<EventModel>(`/events/${organiserId}/${id}/`).query.once('value')
    const previousData = snapshot.exists() ? snapshot.val() : null
    return previousData
  }

  async fetchAllByDateRange(params: { from: number; to?: number; organiserId?: string }): Promise<EventModel[]> {
    const events: EventModel[] = []
    let pageToken: string | undefined
    for (let page = 0; page < EventService.MAX_PAGES; page++) {
      const response = await this.fetchByDateRangePaged({ ...params, pageToken, pageSize: 100 })
        .pipe(take(1))
        .toPromise()
      if (!response) {
        break
      }
      events.push(...response.items)
      if (!response.nextPageToken) {
        break
      }
      pageToken = response.nextPageToken
    }
    return events
  }
}
